<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $tc('accounts.account', 2) }}</h1>
      <div class="details-component__header_right">

          <a v-if="!isAccountFormOpened" class="link" v-on:click="toggleAccountForm">
            {{ $t('accounts.add_account') }}
            <i class="icon icon--plus"></i>
          </a>

          <a v-else class="disabled link" v-on:click="toggleAccountForm" v-bind:class="{ active: isActive }">
            {{ $t('common.close') }}
            <i class="icon icon--cross"></i>
          </a>

        </div>
    </div>
    <div class="details-component__add-new-form"
       v-if="isAccountFormOpened"
       v-bind:class="{ active: isActive }">
      <form class="form">

         <div class="form__field" :class="{'form__field_error' : errors.has('email')}">
          <label for="email" class="form__label">{{ $t('common.email') }}</label>
          <input type="text"
            id="email"
            class="form__input"
            v-model="form.email"
            v-validate
            data-vv-rules="required|email"
            name="email" />
         </div>

         <div class="form__buttons buttonset">
          <button
            type="submit"
            class="buttonset__button btn btn--main"
            v-on:click.prevent.stop="addAccount()">
            {{ $t('products.submit') }}
         </button>
        </div>
      </form>
    </div>

    <div class="details-component__content content" v-if="users">
      <v-client-table :data="users" :columns="columns" :options="options"></v-client-table>
    </div>

     <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelAccountRemove" @confirmed="removeAccount">
      <h1 slot="header">{{ $t('accounts.removing') }}: {{ removeAccountName }} </h1>
      <p slot="body">{{ $t('accounts.confirm_remove') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ accountWarningHeader }}</h1>
      <p slot="body">{{ accountWarningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
  import ConfirmationModal from 'common/ConfirmationModal';
  import WarningModal from 'common/WarningModal';
  import { mapGetters } from 'vuex';
  import * as mTypes from 'store/mutation-types';
  import moment from 'moment';

  export default {
    props: ['users', 'landlord'],
    components: {
      ConfirmationModal,
      WarningModal,
    },
    computed: mapGetters({
      landlordError: 'landlords/landlord/error',
      accountError: 'accounts/account/error',
      user: 'user/user/user',
      account_creation: 'landlords/landlord/account_creation',
      account: 'accounts/account/account',
    }),
    methods: {
      showConfirmationForRemovingAccount(row) {
        this.removeAccountId = row.id;
        this.removeAccountName = row.email;
        this.showConfirmationModal = true;
      },
      cancelAccountRemove() {
        this.showConfirmationModal = false;
        this.removeAccountId = null;
        this.removeAccountName = null;
      },
      closeWarningModal() {
        this.showWarningModal = false;
        this.accountWarningHeader = null;
        this.accountWarningBody = null;
        this.$store.commit(`accounts/account/${mTypes.CLEAR_STATE}`);
      },
      removeAccount() {
        this.$store.dispatch('accounts/account/delete', { id: this.removeAccountId, partner: this.$route.params.id, user: this.user })
          .then(() => {
            this.showConfirmationModal = false;
            if (!this.accountError) {
              this.$store.commit(`landlords/landlord/${mTypes.REMOVE_USER}`, this.removeAccountId);
            } else {
              this.accountWarningHeader = this.$t('accounts.removing_error_header');
              this.accountWarningBody = this.$t('accounts.removing_error');
              this.showWarningModal = true;
            }
            this.removeAccountId = null;
            this.removeAccountName = null;
          });
      },
      toggleAccountForm() {
        this.isAccountFormOpened = !this.isAccountFormOpened;
        this.isActive = !this.isActive;

        if (!this.isAccountFormOpened) {
          this.form = {};
          this.$store.commit(`accounts/account/${mTypes.CLEAR_STATE}`);
        }
      },
      addAccount() {
        this.$validator.validateAll()
          .then((success) => {
            if (!success) {
              return;
            }
            this.form.partner_id = this.$route.params.id;
            this.$store.dispatch('accounts/account/save', { account: this.form, user: this.user }).then(() => {
              if (this.accountError) {
                if (this.accountError === 'account_already_exists') {
                  this.accountWarningBody = this.$t('accounts.create_error_exists', { email: this.form.email });
                } else {
                  this.accountWarningBody = this.$t('accounts.create_error');
                }
                this.accountWarningHeader = this.$t('accounts.create_error_header');
                this.showWarningModal = true;
              } else {
                this.$store.commit(`landlords/landlord/${mTypes.ADD_USER}`, this.account);
              }
              this.toggleAccountForm();
            }).catch(() => {
              this.accountWarningHeader = this.$t('accounts.create_error_header');
              if (this.accountError === 'account_already_exists') {
                this.accountWarningBody = this.$t('accounts.create_error_exists', { email: this.form.email });
              } else {
                this.accountWarningBody = this.$t('accounts.create_error');
              }
              this.showWarningModal = true;
            });
          });
      },
      activateTenantAccount(userId) {
        this.$store.dispatch('landlords/landlord/activateLandlordAccount', { user: userId }).then(() => {
          this.showModal = true;
        });
      },
      formatDate(value) {
        if (!value) {
          return moment().format('DD.MM.YYYY');
        }
        return value;
      },
    },
    data() {
      return {
        showConfirmationModal: false,
        showWarningModal: false,
        removeAccountId: null,
        removeAccountName: null,
        accountWarningBody: null,
        accountWarningHeader: null,
        isAccountFormOpened: false,
        isActive: false,
        form: {},
        columns: ['email', 'createdAt', 'edit_account', 'notify', 'remove_account'],
        options: {
          filterable: false,
          rowClassCallback: () => 'VueTables__row',
          headings: {
            email: () => this.$t('common.email'),
            createdAt: () => this.$t('common.created_at'),
            notify: () => (''),
            edit_account: () => (''),
            remove_account: () => (''),
          },
          texts: {
            noResults: this.$t('tables.noRecords'),
            count: this.$t('tables.count'),
          },
          templates: {
            remove_account: (h, row) => (
              <div class="delete-wrapper" v-show="row.email !== this.tenant.email">
                <a on-click={ () => this.showConfirmationForRemovingAccount(row) }>
                  <span class="delete-wrapper__icon icon icon--cross"></span>
                </a>
              </div>
            ),
            email: (h, row) => (
              <span>{row.email}</span>
            ),
            createdAt: (h, row) => (
              this.formatDate(row.createdAt)
            ),
            notify: (h, row) => (
              <button v-show={ !row.account_created && this.account_creation}
                type="submit"
                class="buttonset__button btn btn--main btn--small"
                on-click={ () => this.activateTenantAccount(row.id) }>
                { this.$t('tenant_details.create_account') }
              </button>
            ),
          },
        },
      };
    },
  };
</script>
<style lang="scss" scoped>
  @import '~styles/blocks/details.scss';
  .details-component__listWidget {
    width: 50%;
  }

  .link {
    .icon--plus {
      position: relative;
      top: -1px;
      margin: 0 5px;
      font-weight: 900;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/landlords/AccountsTable.vue